import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Button,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Checkbox,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import Parse from 'parse'
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Sync as SyncIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Send as SendIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material'
import {
  CompanyUser,
  CompanyUserRole,
  companyUserHasOutstandingInvitation,
  companyUserStatus,
} from '../../../../core/models/Company'
import { CompanyWithStats } from '../../types'
import { getRoleColor } from '../../types'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledButton,
} from '../../styles/StyledComponents'
import { BillingImpactPreview } from '../../../Billing/BillingImpactPreview'
import { BillingStatusIndicator } from '../../../Billing/BillingStatusIndicator'
import { PendingUserManager } from '../../../Billing/PendingUserManager'
import { CompanyService } from '../../../../services/CompanyService'
import { addNotification } from '../../../../stores/NotificationStore'
import store from '../../../../stores'
import { alpha, useTheme } from '@mui/material/styles'
import { getDisplayName } from '../../../../utils/personName'
import {
  useAnyPermission,
  useGlobalAdmin,
  usePermission,
  useSurface,
} from '../../../../hooks/usePermission'
import { useRbacStore } from '../../../../stores/rbacStore'
import { memberRoleLabel } from '../../utils/roleOptions'
import { ManageAccessDrawer, type ManageAccessMember } from '../access/ManageAccessDrawer'
import { TransferOwnershipDialog } from '../access/TransferOwnershipDialog'
import { CompanyOwnersSection } from '../access/CompanyOwnersSection'

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]
const DEFAULT_ROWS_PER_PAGE = 25

interface UsersDialogProps {
  open: boolean
  onClose: () => void
  company: CompanyWithStats | null
  users: CompanyUser[]
  onEditUser: (user: CompanyUser) => void
  onRemoveUser: (user: CompanyUser) => void
  onInviteUser: () => void
  onSyncUnassignedUsers: () => void
  loading: boolean
  syncingUsers: boolean
  // Menu management props
  userMenuAnchor: HTMLElement | null
  selectedUserForMenu: CompanyUser | null
  onUserMenuOpen: (event: React.MouseEvent<HTMLElement>, user: CompanyUser) => void
  onUserMenuClose: () => void
  onRemoveUserFromMenu: () => void
  onEditUserFromMenu: () => void
  // Callback to refresh user list
  onRefreshUsers?: () => void
}

export const UsersDialog: React.FC<UsersDialogProps> = ({
  open,
  onClose,
  company,
  users,
  onEditUser,
  onRemoveUser,
  onInviteUser,
  onSyncUnassignedUsers,
  loading,
  syncingUsers,
  userMenuAnchor,
  selectedUserForMenu,
  onUserMenuOpen,
  onUserMenuClose,
  onRemoveUserFromMenu,
  onEditUserFromMenu,
  onRefreshUsers,
}) => {
  const theme = useTheme()
  const primaryHex = theme.palette.primary.main
  // Gated by what the person may do in this company (SC-439); the server checks every action too.
  const canInvite = useSurface('users-dialog.invite-button')
  // What a person lacks permission for is hidden, not shown disabled (SC-439, phase F): the
  // billing chip needs billing.view_plan, and Pending Users the same key as Invite (cloud's
  // pending-user calls ask for members.invite), so no request is made without them.
  const canSeeBillingStatus = useSurface('users-dialog.billing-status')
  const canOpenPendingUsers = useSurface('users-dialog.pending-users')
  // Cloud's `syncUnassignedUsers` needs members.invite; the Invite button is hidden without it.
  const canSyncUnassigned = useSurface('users-dialog.sync-unassigned')
  // Either right opens the drawer ('manage-access.drawer'): one changes the role, the other
  // single permissions, and the drawer locks whatever the person may not change.
  const canManageAccess = useAnyPermission(['roles.assign', 'permissions.manage'])
  // The store holds the viewer's own company, and an owner-only key is not covered by platform-admin
  // elevation, so on another company's roster (platform admin view) the server would answer 403.
  const rbacCompanyId = useRbacStore((s) => s.companyId)
  // Names the screen already holds, for rows that carry a key but no name; never fetched here.
  const knownRoles = useRbacStore((s) => s.catalogue?.systemRoles)
  const canTransferOwnership =
    useSurface('users-dialog.transfer-ownership') && rbacCompanyId === company?.id
  // Owners are managed only through the platform owner API, which needs the global-admin gate, so
  // the section is for a server-verified global admin and not for a company's own Owner or Admin
  // (the server checks each call too).
  const canManageOwners = useGlobalAdmin()
  // A role change needs roles.assign and the other fields members.edit ('users-dialog.edit-user'),
  // so either opens the dialog, which hides the role picker without roles.assign.
  const canEditFields = useSurface('users-dialog.edit-user')
  const canAssignRoles = usePermission('roles.assign')
  const canEditUser = canEditFields || canAssignRoles
  const canRemoveUser = useSurface('users-dialog.remove-user')
  const [accessMember, setAccessMember] = useState<ManageAccessMember | null>(null)
  const [transferMember, setTransferMember] = useState<ManageAccessMember | null>(null)
  const currentUserId = Parse.User.current()?.id ?? null
  // Billing state
  const [billingImpact, setBillingImpact] = useState<any>(null)
  const [loadingBillingImpact, setLoadingBillingImpact] = useState(false)
  const [showBillingPreview, setShowBillingPreview] = useState(false)
  const [billingPreviewAction, setBillingPreviewAction] = useState<'addition' | 'removal'>('addition')
  const [selectedUserForBilling, setSelectedUserForBilling] = useState<CompanyUser | null>(null)
  const [processingBilling, setProcessingBilling] = useState(false)
  const [showPendingUserManager, setShowPendingUserManager] = useState(false)

  // Resend / password-reset feedback
  const [resendBusyUserId, setResendBusyUserId] = useState<string | null>(null)
  const [resendNotice, setResendNotice] = useState<{
    severity: 'success' | 'error'
    message: string
  } | null>(null)

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])

  // Search / filter state
  const [searchQuery, setSearchQuery] = useState('')

  // Pagination state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)

  // Reset pagination + selection when the dialog opens or the user list changes
  useEffect(() => {
    if (open) {
      setPage(0)
      setSelectedUsers([])
      setSearchQuery('')
    }
  }, [open, users.length])

  const memberOf = (user: CompanyUser): ManageAccessMember => ({
    userId: user.user_id,
    name: getDisplayName(
      { ...user.profile, username: user.user?.username },
      user.user?.email || 'this member'
    ),
    email: user.user?.email,
  })

  const ownerCandidates = useMemo(
    () => users.filter((u) => companyUserStatus(u) === 'active').map(memberOf),
    [users]
  )

  // ─── Filtering ───────────────────────────────────────────────────────────────

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users
    const q = searchQuery.toLowerCase()
    return users.filter(u => {
      const fullName = `${u.profile?.firstName || ''} ${u.profile?.lastName || ''} ${u.profile?.displayName || ''}`.toLowerCase()
      const email = (u.user?.email || '').toLowerCase()
      const username = (u.user?.username || '').toLowerCase()
      const role = memberRoleLabel(u, u.role || '', knownRoles).toLowerCase()
      const dept = (u.department || '').toLowerCase()
      return (
        fullName.includes(q) ||
        email.includes(q) ||
        username.includes(q) ||
        role.includes(q) ||
        dept.includes(q)
      )
    })
  }, [users, searchQuery, knownRoles])

  // ─── Pagination ───────────────────────────────────────────────────────────────

  const paginatedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage],
  )

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // ─── Bulk selection ───────────────────────────────────────────────────────────

  const isAllPageSelected =
    paginatedUsers.length > 0 &&
    paginatedUsers.every(u => selectedUsers.includes(u.user_id))

  const isIndeterminate =
    paginatedUsers.some(u => selectedUsers.includes(u.user_id)) && !isAllPageSelected

  const handleSelectUser = useCallback((userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId],
    )
  }, [])

  const handleSelectAllPage = () => {
    if (isAllPageSelected) {
      const pageIds = new Set(paginatedUsers.map(u => u.user_id))
      setSelectedUsers(prev => prev.filter(id => !pageIds.has(id)))
    } else {
      const pageIds = paginatedUsers.map(u => u.user_id)
      setSelectedUsers(prev => Array.from(new Set([...prev, ...pageIds])))
    }
  }

  // ─── Billing helpers ──────────────────────────────────────────────────────────

  const handleInviteUserWithBilling = async () => {
    if (!company) return
    try {
      setLoadingBillingImpact(true)
      setBillingPreviewAction('addition')
      const result = await CompanyService.getBillingImpactPreview(company.id, 'addition', 1)
      if (result.success) {
        setBillingImpact(result.billing_impact)
        setShowBillingPreview(true)
      } else {
        store.dispatch(
          addNotification({
            message: result.error || 'Failed to calculate billing impact',
            type: 'error',
            duration: 5000,
          })
        )
      }
    } catch {
      store.dispatch(
        addNotification({
          message: 'Failed to calculate billing impact',
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      setLoadingBillingImpact(false)
    }
  }

  const handlePendingUsersAdded = () => {
    onRefreshUsers?.()
  }

  const handleResendInvitation = async (member: CompanyUser) => {
    if (!company?.id) return
    setResendBusyUserId(member.user_id)
    setResendNotice(null)
    try {
      const result = await CompanyService.resendInvitation(member.user_id, company.id)
      const who = member.user?.email || 'the user'
      setResendNotice(
        result.success
          ? {
              severity: 'success',
              // Say which it was: the admin needs to know whether the link already in
              // the invitee's inbox still works.
              message: result.tokenReused
                ? `Invitation resent to ${who}. Their original link still works too.`
                : `A new invitation link was sent to ${who}.`,
            }
          : { severity: 'error', message: result.error || `Could not resend to ${who}.` },
      )
      if (result.success) onRefreshUsers?.()
    } finally {
      setResendBusyUserId(null)
    }
  }

  const handleSendPasswordReset = async (member: CompanyUser) => {
    const email = member.user?.email
    if (!email) {
      setResendNotice({ severity: 'error', message: 'This user has no email address on record.' })
      return
    }
    setResendBusyUserId(member.user_id)
    setResendNotice(null)
    try {
      const authService = (await import('../../../../services/authService')).default
      await authService.requestPasswordReset(email)
      // Deliberately "requested", not "sent". POST /api/auth/password-reset/request answers
      // 200 / success:true whether or not the mail went out — when the account does not
      // exist, when sendTransactionalForEvent fails, and even from its own outer catch —
      // because revealing the difference to an anonymous caller is account enumeration. So a
      // resolved promise here means "the request was accepted", and nothing stronger.
      // Claiming delivery told the admin an email had gone out when none had, which is the
      // one thing they cannot check from this screen. [SC-313]
      setResendNotice({
        severity: 'success',
        message: `Password reset requested for ${email}. If the address is registered, a reset link is on its way — ask them to check spam if it does not arrive.`,
      })
    } catch (error: any) {
      setResendNotice({
        severity: 'error',
        message: error?.message || `Could not send a password reset to ${email}.`,
      })
    } finally {
      setResendBusyUserId(null)
    }
  }

  const handleRemoveUserWithBilling = async (user: CompanyUser) => {
    if (!company) return
    try {
      setLoadingBillingImpact(true)
      setBillingPreviewAction('removal')
      setSelectedUserForBilling(user)
      const result = await CompanyService.getBillingImpactPreview(company.id, 'removal', 1)
      if (result.success) {
        setBillingImpact(result.billing_impact)
        setShowBillingPreview(true)
      } else {
        store.dispatch(
          addNotification({
            message: result.error || 'Failed to calculate billing impact',
            type: 'error',
            duration: 5000,
          })
        )
      }
    } catch {
      store.dispatch(
        addNotification({
          message: 'Failed to calculate billing impact',
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      setLoadingBillingImpact(false)
    }
  }

  const handleConfirmBillingAction = async () => {
    if (!company || !billingImpact) return

    try {
      setProcessingBilling(true)

      if (billingPreviewAction === 'addition') {
        onInviteUser()
      } else if (billingPreviewAction === 'removal' && selectedUserForBilling) {
        const result = await CompanyService.removeUserWithBilling(
          company.id,
          selectedUserForBilling.user_id,
          undefined,
          0,
        )
        if (result.success) {
          store.dispatch(
            addNotification({
              message: 'User removed successfully with billing adjustment',
              type: 'success',
              duration: 5000,
            })
          )
          onRefreshUsers?.()
        } else {
          store.dispatch(
            addNotification({
              message: result.error || 'Failed to remove user',
              type: 'error',
              duration: 5000,
            })
          )
        }
      }

      setShowBillingPreview(false)
      setBillingImpact(null)
      setSelectedUserForBilling(null)
    } catch {
      store.dispatch(
        addNotification({
          message: 'Failed to process billing action',
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      setProcessingBilling(false)
    }
  }

  const handleCancelBillingPreview = () => {
    setShowBillingPreview(false)
    setBillingImpact(null)
    setSelectedUserForBilling(null)
  }

  const handleBulkRemoveUsers = async () => {
    if (!company || selectedUsers.length === 0) return
    try {
      setLoadingBillingImpact(true)
      setBillingPreviewAction('removal')
      const result = await CompanyService.getBulkRemovalBillingImpactPreview(company.id, selectedUsers)
      if (result.success) {
        setBillingImpact(result.billing_impact)
        setShowBillingPreview(true)
      } else {
        store.dispatch(
          addNotification({
            message: result.error || 'Failed to calculate bulk removal billing impact',
            type: 'error',
            duration: 5000,
          })
        )
      }
    } catch {
      store.dispatch(
        addNotification({
          message: 'Failed to calculate bulk removal billing impact',
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      setLoadingBillingImpact(false)
    }
  }

  const handleConfirmBulkBillingAction = async () => {
    if (!company || !billingImpact || selectedUsers.length === 0) return
    try {
      setProcessingBilling(true)
      const result = await CompanyService.removeUsersWithBilling(company.id, selectedUsers, undefined, 0)
      if (result.success) {
        store.dispatch(
          addNotification({
            message: `Successfully removed ${result.removed_count} users with billing adjustment`,
            type: 'success',
            duration: 5000,
          })
        )
        setSelectedUsers([])
        onRefreshUsers?.()
      } else {
        store.dispatch(
          addNotification({
            message: result.error || 'Failed to remove users',
            type: 'error',
            duration: 5000,
          })
        )
      }
      setShowBillingPreview(false)
      setBillingImpact(null)
    } catch {
      store.dispatch(
        addNotification({
          message: 'Failed to process bulk billing action',
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      setProcessingBilling(false)
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────────

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <StyledDialogTitle>
        <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '1.25rem', fontWeight: 600 }}>
          Users — {company?.name}
        </Typography>
      </StyledDialogTitle>

      <StyledDialogContent>
        {/* Top bar: counts, search, actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {loading ? 'Loading…' : `${filteredUsers.length} of ${users.length} user${users.length !== 1 ? 's' : ''}`}
            </Typography>
            {selectedUsers.length > 0 && (
              <Chip
                label={`${selectedUsers.length} selected`}
                size="small"
                color="primary"
                onDelete={() => setSelectedUsers([])}
                deleteIcon={<ClearIcon />}
              />
            )}
          </Box>

          {/* Billing Status Indicator */}
          {company && canSeeBillingStatus && (
            <BillingStatusIndicator
              companyId={company.id}
              currentUsers={users.length}
              onRefresh={() => { /* billing-only refresh */ }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {selectedUsers.length > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={loadingBillingImpact ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={handleBulkRemoveUsers}
                disabled={loadingBillingImpact}
                size="small"
              >
                {loadingBillingImpact ? 'Calculating…' : `Remove ${selectedUsers.length} Users`}
              </Button>
            )}
            {canSyncUnassigned && (
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={onSyncUnassignedUsers}
                disabled={syncingUsers}
                size="small"
                sx={{
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': { borderColor: 'primary.light', backgroundColor: alpha(primaryHex, 0.1) },
                }}
              >
                {syncingUsers ? 'Syncing…' : 'Sync Unassigned'}
              </Button>
            )}
            {canInvite && (
              <Button
                variant="contained"
                startIcon={loadingBillingImpact ? <CircularProgress size={16} /> : <AddIcon />}
                onClick={handleInviteUserWithBilling}
                disabled={loadingBillingImpact}
                size="small"
              >
                {loadingBillingImpact ? 'Calculating…' : 'Invite User'}
              </Button>
            )}
            {canOpenPendingUsers && (
              <StyledButton
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={() => setShowPendingUserManager(true)}
                size="small"
              >
                Pending Users
              </StyledButton>
            )}
          </Box>
        </Box>

        {canManageOwners && company?.id && (
          <CompanyOwnersSection
            companyId={company.id}
            companyName={company.name}
            members={ownerCandidates}
            onChanged={() => onRefreshUsers?.()}
          />
        )}

        {/* Search box */}
        <TextField
          placeholder="Search by name, email, role, or department…"
          size="small"
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setPage(0) }}
          sx={{ mb: 2, width: '100%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setSearchQuery(''); setPage(0) }}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {resendNotice && (
          <Alert
            severity={resendNotice.severity}
            onClose={() => setResendNotice(null)}
            sx={{ mb: 2 }}
          >
            {resendNotice.message}
          </Alert>
        )}

        {company?.allowed_domains && company.allowed_domains.length > 0 && (
          <Alert severity="info" sx={{ mb: 2, bgcolor: alpha(theme.palette.info.main, 0.08), border: `1px solid ${alpha(theme.palette.info.main, 0.25)}` }}>
            <Typography variant="body2">
              {company.allowed_domains.length} allowed domain{company.allowed_domains.length !== 1 ? 's' : ''}: {company.allowed_domains.join(', ')}
            </Typography>
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, gap: 2 }}>
            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
            <Typography sx={{ color: 'text.secondary' }}>Loading users…</Typography>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography sx={{ color: 'text.secondary' }}>
              {searchQuery ? 'No users match your search.' : 'No users found.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                bgcolor: 'background.paper',
                backgroundImage: 'none',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: 480,
                overflow: 'auto',
                position: 'relative',
                isolation: 'isolate',
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {/*
                      Opaque sticky header + high z-index: action.hover can be translucent and lets body
                      rows paint over the header while scrolling; password-manager overlays also confuse stacking.
                    */}
                    <TableCell
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 5,
                        width: 48,
                        py: 1,
                        bgcolor: 'background.paper',
                        backgroundImage: 'none',
                        color: 'text.primary',
                        fontWeight: 600,
                        borderBottom: 1,
                        borderColor: 'divider',
                        boxShadow: theme => `0 1px 0 ${theme.palette.divider}`,
                      }}
                    >
                      <Checkbox
                        checked={isAllPageSelected}
                        indeterminate={isIndeterminate}
                        onChange={handleSelectAllPage}
                        sx={{ p: 0 }}
                        size="small"
                      />
                    </TableCell>
                    {(['Name', 'Role', 'Department', 'Title', 'Status', 'Actions'] as const).map((label) => (
                      <TableCell
                        key={label}
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 5,
                          py: 1,
                          bgcolor: 'background.paper',
                          backgroundImage: 'none',
                          color: 'text.primary',
                          fontWeight: 600,
                          borderBottom: 1,
                          borderColor: 'divider',
                          boxShadow: theme => `0 1px 0 ${theme.palette.divider}`,
                          ...(label === 'Actions' ? { width: 56 } : {}),
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map(user => (
                    <TableRow
                      key={user.user_id}
                      sx={{
                        '&:hover': { backgroundColor: alpha(primaryHex, 0.05) },
                        backgroundColor: selectedUsers.includes(user.user_id)
                          ? alpha(primaryHex, 0.08)
                          : 'transparent',
                      }}
                    >
                      <TableCell sx={{ py: 0.75, width: 48 }}>
                        <Checkbox
                          checked={selectedUsers.includes(user.user_id)}
                          onChange={() => handleSelectUser(user.user_id)}
                          sx={{ p: 0 }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', lineHeight: 1.2 }}>
                            {getDisplayName(
                              { ...user.profile, username: user.user?.username },
                              user.user?.email || '—'
                            )}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {user.user?.email || 'No email'}
                          </Typography>
                          {user.auto_assigned && (
                            <Chip
                              label="Auto-assigned"
                              size="small"
                              sx={{
                                mt: 0.25,
                                ml: 0,
                                bgcolor: alpha(theme.palette.success.main, 0.15),
                                color: 'success.main',
                                fontSize: '0.68rem',
                                height: 18,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Chip
                          label={memberRoleLabel(user, user.role, knownRoles)}
                          size="small"
                          sx={{
                            bgcolor: `${getRoleColor(user.role)}15`,
                            color: getRoleColor(user.role),
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.75, color: 'text.primary' }}>{user.department || '—'}</TableCell>
                      <TableCell sx={{ py: 0.75, color: 'text.primary' }}>{user.title || '—'}</TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        {(() => {
                          // "Invited" is the row that has been sent a link but has not
                          // finished onboarding — is_active alone reported those as
                          // Active from the moment the invite was sent. [SC-313]
                          const status = companyUserStatus(user)
                          const chip = {
                            invited: { label: 'Invited', color: 'warning' as const },
                            inactive: { label: 'Inactive', color: 'default' as const },
                            active: { label: 'Active', color: 'success' as const },
                          }[status]
                          return (
                            <Chip
                              label={chip.label}
                              size="small"
                              color={chip.color}
                              sx={{ fontSize: '0.72rem', height: 22 }}
                            />
                          )
                        })()}
                      </TableCell>
                      <TableCell sx={{ py: 0.75, width: 56 }}>
                        <Tooltip title="User actions">
                          <IconButton
                            size="small"
                            onClick={e => onUserMenuOpen(e, user)}
                            sx={{ color: 'text.secondary' }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          </>
        )}
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onClose} variant="outlined">
          Close
        </StyledButton>
      </StyledDialogActions>

      {/* User Actions Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={onUserMenuClose}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        {canEditUser && (
          <MenuItem
            data-testid="user-menu-edit"
            onClick={() => {
              if (selectedUserForMenu) {
                onEditUser(selectedUserForMenu)
                onUserMenuClose()
              }
            }}
            sx={{ color: 'text.primary', '&:hover': { backgroundColor: alpha(primaryHex, 0.1) } }}
          >
            <EditIcon sx={{ mr: 1, fontSize: 16 }} />
            Edit User
          </MenuItem>
        )}
        {canManageAccess && (
          <MenuItem
            data-testid="user-menu-manage-access"
            onClick={() => {
              if (selectedUserForMenu) {
                setAccessMember(memberOf(selectedUserForMenu))
                onUserMenuClose()
              }
            }}
            sx={{ color: 'text.primary', '&:hover': { backgroundColor: alpha(primaryHex, 0.1) } }}
          >
            <ShieldOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
            Manage access
          </MenuItem>
        )}
        {canTransferOwnership &&
          selectedUserForMenu &&
          selectedUserForMenu.user_id !== currentUserId &&
          companyUserStatus(selectedUserForMenu) === 'active' && (
            <MenuItem
              data-testid="user-menu-transfer-ownership"
              onClick={() => {
                setTransferMember(memberOf(selectedUserForMenu))
                onUserMenuClose()
              }}
              sx={{ color: 'text.primary', '&:hover': { backgroundColor: alpha(primaryHex, 0.1) } }}
            >
              <SwapHorizIcon sx={{ mr: 1, fontSize: 16 }} />
              Transfer ownership
            </MenuItem>
          )}
        {/* A row with an unfinished invitation gets a fresh invite; everyone else has
            no pending invite, so a password reset is the useful action. Keyed off the
            invitation token rather than emailVerified or hasManualPassword — see
            companyUserHasOutstandingInvitation for why both of those mislead. */}
        {selectedUserForMenu && companyUserHasOutstandingInvitation(selectedUserForMenu) ? (
          <MenuItem
            disabled={resendBusyUserId === selectedUserForMenu.user_id}
            onClick={() => {
              if (selectedUserForMenu) {
                void handleResendInvitation(selectedUserForMenu)
                onUserMenuClose()
              }
            }}
            sx={{ color: 'text.primary', '&:hover': { backgroundColor: alpha(primaryHex, 0.1) } }}
          >
            <SendIcon sx={{ mr: 1, fontSize: 16 }} />
            Resend invite
          </MenuItem>
        ) : (
          <MenuItem
            disabled={resendBusyUserId === selectedUserForMenu?.user_id}
            onClick={() => {
              if (selectedUserForMenu) {
                void handleSendPasswordReset(selectedUserForMenu)
                onUserMenuClose()
              }
            }}
            sx={{ color: 'text.primary', '&:hover': { backgroundColor: alpha(primaryHex, 0.1) } }}
          >
            <LockResetIcon sx={{ mr: 1, fontSize: 16 }} />
            Send password reset
          </MenuItem>
        )}
        {canRemoveUser && (
          <MenuItem
            data-testid="user-menu-remove"
            onClick={() => {
              if (selectedUserForMenu) {
                handleRemoveUserWithBilling(selectedUserForMenu)
                onUserMenuClose()
              }
            }}
            sx={{ color: 'text.primary', '&:hover': { backgroundColor: alpha(primaryHex, 0.1) } }}
          >
            <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
            Remove User
          </MenuItem>
        )}
      </Menu>

      {/* Billing Impact Preview Dialog */}
      <BillingImpactPreview
        open={showBillingPreview}
        onClose={handleCancelBillingPreview}
        onConfirm={selectedUsers.length > 0 ? handleConfirmBulkBillingAction : handleConfirmBillingAction}
        action={billingPreviewAction}
        user={selectedUserForBilling || undefined}
        billingImpact={billingImpact}
        company={company}
        loading={processingBilling}
        isBulkOperation={selectedUsers.length > 0}
        selectedUserIds={selectedUsers}
      />

      {company?.id && (
        <>
          <ManageAccessDrawer
            open={!!accessMember}
            companyId={company.id}
            member={accessMember}
            currentUserId={currentUserId}
            onClose={() => setAccessMember(null)}
            onSaved={() => {
              // The roster shows roles, and the person's own answer is stale if they were edited.
              void useRbacStore.getState().refresh()
              onRefreshUsers?.()
            }}
          />
          <TransferOwnershipDialog
            open={!!transferMember}
            companyId={company.id}
            member={transferMember}
            onClose={() => setTransferMember(null)}
            onTransferred={() => {
              // The person who confirmed is now an Admin: ask again what they may do.
              void useRbacStore.getState().refresh()
              onRefreshUsers?.()
            }}
          />
        </>
      )}

      <PendingUserManager
        open={showPendingUserManager && canOpenPendingUsers}
        onClose={() => setShowPendingUserManager(false)}
        companyId={company?.id || ''}
        onUsersAdded={handlePendingUsersAdded}
      />
    </StyledDialog>
  )
}
